import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Shield, HeartHandshake, Sparkles, Check, Star, Heart, Moon } from "lucide-react";
import { IconPrivacy, IconReview, IconIntention, CornerBracket } from "@/components/BrandIcons";
import cardOrnament from "@/assets/brand/card-corner-ornament.jpg";
import { BrandEmblem, GoldDivider } from "@/components/BrandEmblem";
import arabesqueTile from "@/assets/brand/arabesque-tile.jpg";
import { api } from "@/lib/api";
import { useRamadan } from "@/hooks/useRamadan";

const EMOTION_PHOTO = "https://customer-assets.emergentagent.com/job_rishta-preview/artifacts/3wpkx16e_41D49A89-ABF7-4862-AFD9-F1B9B9AE10ED.png";
// Warm, realistic photography — between-section imagery
const PORTRAIT_PHOTO = "https://customer-assets-0z36b82j.emergentagent.net/job_rishta-preview/artifacts/eef5b3edbc9455cc_MariaOsamaKhanKhawabeedahWeddingFormals_3.webp"; // bride in emerald green lehenga
const HANDS_PHOTO    = "https://customer-assets.emergentagent.com/job_rishta-preview/artifacts/oh85fkef_IMG_5608.jpeg"; // clasped mehndi hands + bangles
const COUPLE_PHOTO   = "https://customer-assets.emergentagent.com/job_rishta-preview/artifacts/5f8qojjt_IMG_5603.jpeg"; // bride & groom intimate moment

// CMS fallback — used when admin hasn't configured or API not reachable
const CMS_FALLBACK = {
  hero_eyebrow: "Modern rishta · Alvorlige mennesker",
  hero_title_html: "Find din <em class=\"text-gold-600\">naseeb</em> — med familie, tro og rigtige intentioner.",
  hero_description: "Find Your Naseeb forbinder muslimer der leder efter seriøse ægteskaber — fra Pakistan til diasporaen i Danmark, UK, Norge, Sverige, Tyskland, Canada og USA. Hver profil gennemgås. Hver introduktion begynder med intention.",
  hero_cta_primary: "Opret din profil",
  hero_cta_secondary: "Jeg har allerede en konto",
  values_eyebrow: "Det vi beskytter",
  values_title: "Et varmt, seriøst hjem for enhver bror og søster.",
  values_description: "En seriøs ægteskabsrejse fortjener en seriøs platform — én der behandler hvert medlem med lige respekt, privatliv og omhu.",
  how_eyebrow: "En gennemtænkt proces",
  how_title: "Fire rolige skridt — fra hej til nikah.",
  quran_verse: "„Og blandt Hans tegn er, at Han skabte ægtefæller til jer af jer selv, så I kan finde ro hos hinanden; og Han lagde kærlighed og barmhjertighed mellem jer.\"",
  quran_reference: "Qur'anen · Ar-Rum 30:21",
  final_cta_title: "Der er én, der bliver skrevet til dig.",
  final_cta_description: "Din profil, privat opbygget. Dine forslag, omhyggeligt kurateret. Din tid, dybt respekteret. Brødre og søstre — må jeres næste skridt begynde her, med barakah.",
  testimonials_eyebrow: "Alhamdulillah",
  testimonials_title: "Historier fra familier der stolede på processen.",
};

function Sparkles1() {
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      <span className="sparkle lg" style={{ top: "12%", left: "18%", animationDelay: "0s" }} />
      <span className="sparkle" style={{ top: "28%", left: "9%", animationDelay: "0.6s" }} />
      <span className="sparkle sm" style={{ top: "44%", left: "23%", animationDelay: "1.2s" }} />
      <span className="sparkle" style={{ top: "62%", left: "7%", animationDelay: "0.3s" }} />
      <span className="sparkle sm" style={{ top: "80%", left: "16%", animationDelay: "1.8s" }} />
      <span className="sparkle" style={{ top: "20%", right: "12%", animationDelay: "1.4s" }} />
      <span className="sparkle lg" style={{ top: "52%", right: "8%", animationDelay: "0.9s" }} />
      <span className="sparkle sm" style={{ top: "74%", right: "22%", animationDelay: "2.1s" }} />
      <span className="sparkle" style={{ top: "90%", right: "14%", animationDelay: "0.5s" }} />
      <span className="sparkle sm" style={{ top: "8%", right: "35%", animationDelay: "1.7s" }} />
    </div>
  );
}

export default function Landing() {
  const isRamadan = useRamadan();
  const [cms, setCms] = useState(CMS_FALLBACK);

  useEffect(() => {
    api.get("/settings/public").then((r) => {
      const stored = r.data?.landing_cms || {};
      // merge fallback + stored so any missing key keeps its default
      setCms({ ...CMS_FALLBACK, ...stored });
    }).catch(() => { /* keep fallback */ });
  }, []);
  return (
    <div data-testid="landing-page">
      {/* Reusable mihrab (pointed-arch) clip-path for the hero portrait */}
      <svg width="0" height="0" className="absolute" aria-hidden="true">
        <defs>
          <clipPath id="mihrabArch" clipPathUnits="objectBoundingBox">
            <path d="M0,1 L0,0.32 C0,0.14 0.13,0.04 0.3,0.02 C0.42,0.012 0.5,0 0.5,0 C0.5,0 0.58,0.012 0.7,0.02 C0.87,0.04 1,0.14 1,0.32 L1,1 Z" />
          </clipPath>
        </defs>
      </svg>
      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden festive-gradient">
        <Sparkles1 />
        <div className="paisley-corner absolute -top-10 -left-6 w-[340px] h-[340px] -scale-x-100 opacity-80" />
        <div className="paisley-corner absolute -bottom-10 -right-6 w-[340px] h-[340px] opacity-80" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-12 lg:pt-10 lg:pb-14 grid lg:grid-cols-[1.1fr_1fr] gap-10 items-start">
          <div className="animate-fade-up">
            <BrandEmblem size={54} animate className="mb-3" />
            <div className="eyebrow" data-testid="hero-eyebrow">{cms.hero_eyebrow}</div>
            <h1
              className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-serif font-semibold text-olive-700 leading-[1.05] tracking-tight"
              dangerouslySetInnerHTML={{ __html: cms.hero_title_html }}
            />
            <p className="mt-5 text-lg text-brown-600 max-w-xl leading-relaxed">
              {cms.hero_description}
            </p>

            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link to="/register" className="btn-primary" data-testid="hero-cta-register">
                {cms.hero_cta_primary}
              </Link>
              <Link to="/login" className="btn-secondary" data-testid="hero-cta-login">
                {cms.hero_cta_secondary}
              </Link>
            </div>

            <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-brown-500">
              <div className="flex items-center gap-2"><Check className="h-4 w-4 text-olive-500" /> Manuelt godkendte profiler</div>
              <div className="flex items-center gap-2"><Check className="h-4 w-4 text-olive-500" /> Privat. Familie-respektfuld.</div>
              <div className="flex items-center gap-2"><Check className="h-4 w-4 text-olive-500" /> Ingen swipes. Ingen uforpligtende samtaler.</div>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-6 rounded-[2rem] bg-gradient-to-br from-gold-400/40 to-rose-500/20 blur-2xl" />
            {/* Gold arch outline (slightly larger arch behind the image) */}
            <div className="absolute -inset-[5px] mihrab-clip bg-gradient-to-b from-gold-400 via-gold-500 to-gold-600" />
            <div className="relative mihrab-clip float-soft">
              <div className="w-full h-[360px] lg:h-[500px] overflow-hidden bg-sand-200">
                <img
                  src={EMOTION_PHOTO}
                  alt="To hænder forenet — et symbol på forening, mehndi, kærlighed og håb"
                  className="w-full h-full object-cover"
                  style={{ objectPosition: "50% 58%" }}
                  width={1200}
                  height={1200}
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                />
              </div>
              {/* Subtle bottom gradient for caption contrast */}
              <div className="absolute bottom-0 left-0 right-0 h-28 bg-gradient-to-t from-olive-700/80 via-olive-700/25 to-transparent pointer-events-none" />
              <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between text-white">
                <div className="flex items-center gap-2">
                  <Heart className="h-4 w-4 fill-rose-500 text-rose-500 drop-shadow" />
                  <span className="font-script text-3xl drop-shadow leading-none">Barakallahu lakuma</span>
                </div>
                <span className="text-xs uppercase tracking-widest opacity-90">Find Your Naseeb</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <GoldDivider className="w-full max-w-md mx-auto h-9 my-1" />

      {/* ============ VÆRDIER ============ */}
      <section id="values" className="relative py-8">
        <div className="mehndi-tile absolute inset-0 opacity-[0.10] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="eyebrow">{cms.values_eyebrow}</div>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-olive-700 font-medium leading-tight">
              {cms.values_title}
            </h2>
            <p className="mt-4 text-brown-600">
              {cms.values_description}
            </p>
          </div>

          <div className="mt-14 grid md:grid-cols-3 gap-6">
            <ValueCard
              icon={<IconPrivacy className="h-6 w-6" />}
              title="Privat som udgangspunkt"
              desc="Fotos er slørede for gratis medlemmer. Dine oplysninger deles aldrig uden dit samtykke."
            />
            <ValueCard
              icon={<IconReview className="h-6 w-6" />}
              title="Gennemgås med omhu"
              desc="Hver profil — søster eller bror — verificeres af vores team før den bliver aktiv. Vi holder standarden høj."
              accent
            />
            <ValueCard
              icon={<IconIntention className="h-6 w-6" />}
              title="Intention først"
              desc="Kompatibilitet bygges på deen, værdier og livsfase — aldrig på et foto alene."
            />
          </div>
        </div>
      </section>

      {/* ============ VARM MELLEM-STRIPE (Portræt) ============ */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid lg:grid-cols-[1fr_1.05fr] gap-10 lg:gap-16 items-center">
            {/* Image */}
            <div className="relative order-2 lg:order-1">
              <div className="absolute -inset-5 rounded-[2rem] bg-gradient-to-br from-gold-400/30 to-rose-500/15 blur-2xl" />
              <div className="relative rounded-[2rem] overflow-hidden border-4 border-white shadow-medium ring-1 ring-gold-500/30">
                <img
                  src={PORTRAIT_PHOTO}
                  alt="Elegant brud i smaragdgrøn lehenga — værdighed, skønhed og tradition"
                  className="w-full h-[420px] lg:h-[540px] object-cover"
                  style={{ objectPosition: "50% 18%" }}
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-olive-700/70 via-olive-700/10 to-transparent pointer-events-none" />
                <div className="absolute bottom-4 left-5 right-5 flex items-center text-white/95">
                  <span className="font-script text-2xl drop-shadow">med værdighed</span>
                </div>
              </div>
              {/* Small caption card — floats bottom-right */}
              <div className="hidden sm:flex absolute -bottom-6 -right-4 lg:-right-6 bg-white rounded-2xl shadow-medium border border-sand-300 px-5 py-3 items-center gap-3">
                <Heart className="h-4 w-4 fill-rose-500 text-rose-500" />
                <div className="text-xs text-brown-600 leading-tight">
                  <div className="font-serif text-olive-700 text-sm">Respekt først.</div>
                  <div>Altid.</div>
                </div>
              </div>
            </div>

            {/* Text */}
            <div className="order-1 lg:order-2">
              <div className="eyebrow">Et varmt hjem</div>
              <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-olive-700 font-medium leading-tight">
                Et sted hvor du ikke skal bevise dig selv, kun være ærlig om hvem du er.
              </h2>
              <p className="mt-4 text-brown-600 leading-relaxed max-w-xl">
                Find Your Naseeb er skabt til søstre og brødre der leder efter en livspartner
                med ro. Ingen pres. Ingen swipes. Kun rolige skridt — delt med familien,
                beskyttet med privatliv, og båret af den samme intention som samlede vores
                forældre før os.
              </p>
              <div className="mt-8 grid grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <span className="mt-1 inline-flex h-8 w-8 items-center justify-center rounded-full bg-olive-100 text-olive-600 shrink-0">
                    <Shield className="h-4 w-4" />
                  </span>
                  <div className="text-sm text-brown-600 leading-relaxed">
                    <div className="font-serif text-olive-700 text-base">Sikker og privat</div>
                    Dine fotos og detaljer beskyttes som udgangspunkt.
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="mt-1 inline-flex h-8 w-8 items-center justify-center rounded-full bg-gold-500/20 text-gold-600 shrink-0">
                    <HeartHandshake className="h-4 w-4" />
                  </span>
                  <div className="text-sm text-brown-600 leading-relaxed">
                    <div className="font-serif text-olive-700 text-base">Familie-venlig</div>
                    Del diskret enhver profil med mor, far eller wali.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ SÅDAN VIRKER DET ============ */}
      <section id="how" className="bg-sand-100/80 border-y border-sand-300 py-9 relative overflow-hidden pattern-veil">
        <div className="pattern-veil-layer" style={{ backgroundImage: `url(${arabesqueTile})` }} aria-hidden="true" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[1.1fr_1fr] gap-14 items-start">
          <div>
            <div className="eyebrow">{cms.how_eyebrow}</div>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-olive-700 font-medium leading-tight">
              {cms.how_title}
            </h2>
            <ol className="mt-10 space-y-6">
              <Step n="01" title="Opret din profil" text="Del hvem du er — med ærlighed og ro. Tro, familie, drømme og den partner du søger. Gem som kladde indtil du er klar." />
              <Step n="02" title="Bliv gennemgået" text="Vores team verificerer hver profil. De fleste gennemgås inden for 24 timer og bliver aktive med en varm velkomst." />
              <Step n="03" title="Mød dine forslag" text="Kuraterede introduktioner baseret på reel kompatibilitet — ikke swipes. Vis interesse, eller takk nej med respekt." />
              <Step n="04" title="Start en samtale" text="Når interessen er gensidig, åbnes en privat, filtreret samtale — kun for seriøse medlemmer." />
            </ol>
          </div>

          <aside className="grid grid-cols-2 gap-4">
            <StatCard big="92%" label="Profiler godkendt" sub="af oprigtige ansøgninger passerer vores gennemgang" />
            <StatCard big="24t" label="Typisk gennemgangstid" sub="fra indsendelse til aktiv" accent />
            <StatCard big="50/50" label="Søstre & brødre" sub="balanceret fællesskab af seriøse medlemmer" accent />
            <StatCard big="100%" label="Manuelt verificeret" sub="hver profil er menneske-gennemgået, altid" />
            <div className="col-span-2 card-soft p-0 overflow-hidden relative group">
              <div className="grid grid-cols-[1fr_1.2fr]">
                <div className="relative">
                  <img
                    src={HANDS_PHOTO}
                    alt="To hænder mødes — mehndi, bangles, et løfte"
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ objectPosition: "50% 45%" }}
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-white/95" />
                </div>
                <div className="p-6 relative">
                  <div className="paisley-corner absolute -top-6 -right-10 w-[160px] h-[160px] opacity-25" />
                  <div className="flex items-center gap-2 relative">
                    <Star className="h-4 w-4 text-gold-500" />
                    <span className="eyebrow">Familien med</span>
                  </div>
                  <div className="relative mt-2">
                    <span className="font-serif text-lg text-olive-700 block">
                      Bygget med din familie i tankerne.
                    </span>
                    <p className="mt-1.5 text-sm text-brown-600 leading-relaxed">
                      Del diskret enhver profil med en forælder eller værge. Vi tror på at de
                      bedste ægteskaber begynder som en familiebeslutning — aldrig et privat swipe.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* ============ KÆRLIGHEDSBÅND · Letterbox ============ */}
      <section className="relative py-9">
        <div className="max-w-6xl mx-auto">
          <div className="relative overflow-hidden letterbox">
            <div className="relative overflow-hidden gold-gradient text-white px-6 py-16 sm:py-20 text-center">
              <div className="absolute inset-0 bg-[#1C3B32]/30" />
              <Sparkles1 />
              <div className="paisley-corner absolute -top-8 -left-8 w-[280px] h-[280px] opacity-40 invert" />
              <div className="paisley-corner absolute -bottom-8 -right-8 w-[280px] h-[280px] -scale-x-100 opacity-40 invert" />
              <div className="relative animate-fade-up">
                <span className="eyebrow !text-white/85 tracking-[0.25em]">Et vers vi holder nær</span>
                <p className="mt-6 font-serif italic text-[1.6rem] sm:text-3xl lg:text-[2.6rem] leading-snug max-w-3xl mx-auto text-white drop-shadow-md">
                  {cms.quran_verse}
                </p>
                <div className="mt-7 flex items-center justify-center gap-3 text-white/85">
                  <span className="h-px w-10 bg-white/30" />
                  <span className="text-xs tracking-[0.3em] uppercase">{cms.quran_reference}</span>
                  <span className="h-px w-10 bg-white/30" />
                </div>
                <div className="mt-8">
                  <Link to="/register" className="inline-flex items-center justify-center rounded-full bg-white text-olive-700 hover:bg-sand-50 px-8 py-3 font-medium transition-all shadow-soft hover:shadow-medium hover:-translate-y-0.5">
                    Begynd, med barakah
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ UDTALELSER ============ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Warm lead-in photo */}
        <div className="relative mb-16 max-w-3xl mx-auto">
          <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-br from-gold-400/20 via-rose-500/10 to-gold-400/20 blur-2xl" />
          <div className="relative rounded-[2rem] overflow-hidden border-4 border-white shadow-medium ring-1 ring-gold-500/30">
            <img
              src={COUPLE_PHOTO}
              alt="Et ægtepar i deres nikah-øjeblik — varmt, intimt og ægte"
              className="w-full h-[320px] sm:h-[400px] object-cover"
              style={{ objectPosition: "50% 42%" }}
              loading="lazy"
              decoding="async"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-olive-700/85 via-olive-700/20 to-transparent pointer-events-none" />
            <div className="absolute bottom-6 left-6 right-6 text-white">
              <div className="flex items-center gap-2 text-gold-400 text-[10px] uppercase tracking-[0.3em]">
                <Sparkles className="h-3 w-3" /> Fra nikah til et liv sammen
              </div>
              <p className="mt-2 font-script text-3xl sm:text-4xl leading-tight drop-shadow">
                Må Allah gøre det nemt, varmt og fyldt med barakah.
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-2xl">
          <div className="eyebrow">{cms.testimonials_eyebrow}</div>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-olive-700 font-medium leading-tight">
            {cms.testimonials_title}
          </h2>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          <Testimonial name="Aisha &amp; Yusuf" city="København" text="Vi satte pris på at intet føltes forhastet. Gennemgangs- og kompatibilitetstilgangen afspejlede vores værdier smukt." />
          <Testimonial name="Fatima &amp; Ibrahim" city="Aarhus" text="Det føltes som en moderne matchmaker der faktisk lyttede. Ikke en dating-app i forklædning." />
          <Testimonial name="Nusayba &amp; Hamza" city="Odense" text="Vores familier var involveret fra den første besked. Det ændrede alt for os." />
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="relative overflow-hidden mx-4 sm:mx-8 mb-16 rounded-3xl border border-gold-500/40 bg-olive-700">
        <Sparkles1 />
        <div className="paisley-corner absolute -top-6 -left-6 w-[320px] h-[320px] opacity-25 invert" />
        <div className="paisley-corner absolute -bottom-6 -right-6 w-[320px] h-[320px] -scale-x-100 opacity-25 invert" />

        <div className="relative max-w-3xl mx-auto text-center py-16 px-6">
          <div className="inline-flex items-center gap-2 text-gold-400">
            <Sparkles className="h-4 w-4" />
            <span className="text-xs tracking-[0.3em] uppercase">Mubarak på forhånd</span>
            <Sparkles className="h-4 w-4" />
          </div>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-white font-medium leading-tight">
            {cms.final_cta_title}
          </h2>
          <p className="mt-5 text-sand-100/90 max-w-xl mx-auto">
            {cms.final_cta_description}
          </p>
          <div className="mt-9 flex flex-wrap justify-center gap-3">
            <Link to="/register" className="inline-flex items-center justify-center rounded-full bg-gold-500 hover:bg-gold-600 px-9 py-3 text-olive-700 font-medium transition-all shadow-medium" data-testid="cta-bottom-register">
              Opret din profil
            </Link>
            <Link to="/pricing" className="inline-flex items-center justify-center rounded-full border border-white/30 bg-white/5 hover:bg-white/10 px-9 py-3 text-white transition-all" data-testid="cta-bottom-pricing">
              Se medlemskab
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function ValueCard({ icon, title, desc, accent = false }) {
  return (
    <div className={`relative overflow-hidden rounded-2xl shadow-soft transition-all hover:-translate-y-0.5 hover:shadow-medium ${accent ? "bg-gradient-to-br from-gold-100/45 to-sand-50" : "bg-sand-50"}`}>
      {/* Arabesque lattice + vine ornament — top-right. `darken` blend replaces
          the ornament's light cream areas with the card's own cream (no seam),
          keeping only the darker gold linework visible. */}
      <div
        className="absolute inset-0 bg-no-repeat pointer-events-none"
        style={{
          backgroundImage: `url(${cardOrnament})`,
          backgroundPosition: "top right",
          backgroundSize: "auto 100%",
          mixBlendMode: "darken",
          opacity: 0.9,
        }}
        aria-hidden="true"
      />
      {/* Thin gold frame */}
      <div className="absolute inset-[11px] border border-gold-500/45 rounded-lg pointer-events-none" />
      {/* Gold corner brackets */}
      <CornerBracket className="absolute top-2 left-2 w-9 h-9 text-gold-500" />
      <CornerBracket className="absolute top-2 right-2 w-9 h-9 text-gold-500 -scale-x-100" />
      <CornerBracket className="absolute bottom-2 left-2 w-9 h-9 text-gold-500 -scale-y-100" />
      <CornerBracket className="absolute bottom-2 right-2 w-9 h-9 text-gold-500 scale-x-[-1] scale-y-[-1]" />
      {/* Content */}
      <div className="relative p-8">
        <span className={`inline-flex h-12 w-12 items-center justify-center rounded-full ${accent ? "bg-gold-500/15 text-gold-600" : "bg-olive-100 text-olive-600"}`}>
          {icon}
        </span>
        <h3 className="mt-6 font-serif text-2xl text-olive-700">{title}</h3>
        <p className="mt-3 text-brown-600 text-sm leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}

function Step({ n, title, text }) {
  return (
    <li className="flex gap-5">
      <div className="shrink-0 w-12 h-12 rounded-full border border-gold-500/70 text-gold-600 font-serif text-lg flex items-center justify-center bg-gold-100 shadow-soft">
        {n}
      </div>
      <div>
        <div className="font-serif text-xl text-olive-700">{title}</div>
        <p className="mt-1 text-brown-600 text-sm leading-relaxed max-w-md">{text}</p>
      </div>
    </li>
  );
}

function StatCard({ big, label, sub, accent = false }) {
  return (
    <div className={`card-soft p-6 ${accent ? "bg-gradient-to-br from-gold-100/60 to-white border-gold-500/30" : ""}`}>
      <div className="font-serif text-3xl sm:text-4xl text-olive-700">{big}</div>
      <div className="mt-1 eyebrow text-olive-600">{label}</div>
      <div className="mt-2 text-xs text-brown-500 leading-relaxed">{sub}</div>
    </div>
  );
}

function Testimonial({ name, city, text }) {
  return (
    <div className="card-soft bg-gradient-to-br from-sand-100/80 to-white p-8 relative">
      <div className="absolute -top-3 left-6 text-gold-500 font-serif text-5xl leading-none select-none">„</div>
      <p className="italic text-brown-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: text }} />
      <div className="marigold-divider h-5 my-5 opacity-80" />
      <div className="flex items-center justify-between text-sm">
        <span className="font-serif text-olive-700" dangerouslySetInnerHTML={{ __html: name }} />
        <span className="text-brown-500">{city}</span>
      </div>
    </div>
  );
}
